/**
 * <pdp-gallery> — the 2026 product-page coverflow hero.
 *
 * Geometry lives entirely in CSS custom properties (see assets/pdp-body.css);
 * this class only decides which slide is active and hands the offsets to CSS.
 * That is why it is ~150 lines rather than a Swiper config: reproducing the
 * design's flat 25.3% side scale at a fixed centre-to-centre pitch would mean
 * overriding Swiper's transform on every frame anyway.
 *
 * Deliberately does NOT use the .js-media-list / .js-media-sublist class names,
 * so the theme's old gallery bootstraps in global.js stay inert.
 */
class PdpGallery extends HTMLElement {
	connectedCallback() {
		if (this.initialized) return;
		this.initialized = true;

		this.track = this.querySelector('.pdp-gallery__track');
		this.slides = Array.from(this.querySelectorAll('.pdp-gallery__slide'));
		this.liveRegion = this.querySelector('[data-pdp-live]');

		if (!this.track || this.slides.length === 0) return;

		this.count = this.slides.length;
		this.canLoop = this.count >= 3;
		this.sectionId = this.dataset.section;
		this.index = Math.min(
			Math.max(parseInt(this.dataset.initial, 10) || 0, 0),
			this.count - 1
		);

		this.onVariantChange = this.onVariantChange.bind(this);
		document.addEventListener('variant:change', this.onVariantChange);

		this.addEventListener('keydown', (event) => {
			if (event.key === 'ArrowRight') {
				this.next();
				event.preventDefault();
			} else if (event.key === 'ArrowLeft') {
				this.previous();
				event.preventDefault();
			}
		});

		const prev = this.querySelector('[data-pdp-prev]');
		const next = this.querySelector('[data-pdp-next]');
		if (prev) prev.addEventListener('click', () => this.previous());
		if (next) next.addEventListener('click', () => this.next());

		// Clicking a side slide centres it; the centre slide's zoom button opens
		// the modal. Capture phase, so that when a drag has just ended this can
		// swallow the click before it reaches the zoom button underneath.
		this.track.addEventListener(
			'click',
			(event) => {
				if (this.suppressClick) {
					event.preventDefault();
					event.stopPropagation();
					return;
				}
				const slide = event.target.closest('.pdp-gallery__slide');
				if (slide && !slide.classList.contains('is-active')) {
					event.preventDefault();
					event.stopPropagation();
					this.goTo(parseInt(slide.dataset.index, 10));
				}
			},
			true
		);

		this.bindColourTiles();
		this.bindDrag();
		this.render();

		// Hidden slides ship without a source; fetch them once the page is in, so a fast swipe never lands on an empty slide.
		const hydrateAll = () => this.slides.forEach((slide) => this.hydrate(slide.querySelector('img')));
		if (document.readyState === 'complete') {
			hydrateAll();
		} else {
			window.addEventListener('load', hydrateAll, { once: true });
		}
	}

	hydrate(image) {
		if (!image || !image.dataset.srcset) return;
		image.srcset = image.dataset.srcset;
		if (image.dataset.src) image.src = image.dataset.src;
		image.removeAttribute('data-srcset');
		image.removeAttribute('data-src');
	}

	disconnectedCallback() {
		document.removeEventListener('variant:change', this.onVariantChange);
	}

	/**
	 * Colour tiles live outside this element, and when a colour + size pair
	 * resolves to no variant the theme calls setUnavailable() and never fires
	 * variant:change — so the hero needs its own listener to still move.
	 */
	bindColourTiles() {
		const root = document.getElementById(`MainProduct-${this.sectionId}`);
		if (!root) return;
		root.querySelectorAll('[data-pdp-goto-media]').forEach((tile) => {
			tile.addEventListener('click', () => {
				this.goToMediaId(tile.dataset.pdpGotoMedia);
			});
		});
	}

	wrap(index) {
		if (!this.canLoop) {
			return Math.min(Math.max(index, 0), this.count - 1);
		}
		return ((index % this.count) + this.count) % this.count;
	}

	/** Shortest signed distance from the active slide, wrapping when looping. */
	offsetOf(index) {
		let delta = index - this.index;
		if (this.canLoop) {
			if (delta > this.count / 2) delta -= this.count;
			if (delta < -this.count / 2) delta += this.count;
		}
		return delta;
	}

	render() {
		this.slides.forEach((slide, index) => {
			const delta = this.offsetOf(index);
			const distance = Math.abs(delta);

			slide.style.setProperty('--dx', Math.max(-2, Math.min(2, delta)));
			slide.classList.toggle('is-active', delta === 0);
			slide.classList.toggle('is-near', distance === 1);
			slide.setAttribute('aria-hidden', distance > 1 ? 'true' : 'false');

			// Promote neighbours out of lazy loading so the next move is instant.
			if (distance <= 1) {
				const image = slide.querySelector('img');
				if (image) {
					image.loading = 'eager';
					this.hydrate(image);
				}
			}
		});

		/*
		 * Below 1100px the track's height is derived from the active photo's aspect
		 * ratio, and absolutely positioned slides cannot give it one. Liquid seeds
		 * this for the first paint; from here on it follows the active slide. Same
		 * contract as --dx above: decide what is active, hand a number to CSS.
		 */
		const activeRatio = this.slides[this.index] && this.slides[this.index].dataset.ratio;
		if (activeRatio) {
			this.style.setProperty('--pdp-active-ratio', activeRatio);
		}

		if (this.liveRegion) {
			this.liveRegion.textContent = `${this.index + 1} / ${this.count}`;
		}
	}

	goTo(index) {
		const target = this.wrap(index);
		if (target === this.index) return;
		this.index = target;
		this.render();
	}

	goToMediaId(mediaId) {
		const index = this.slides.findIndex(
			(slide) => slide.dataset.mediaId === String(mediaId)
		);
		if (index > -1) this.goTo(index);
	}

	next() {
		this.goTo(this.index + 1);
	}

	previous() {
		this.goTo(this.index - 1);
	}

	onVariantChange(event) {
		if (!event.detail || event.detail.sectionId !== this.sectionId) return;

		const variant = event.detail.variant;
		if (!variant) return;

		if (variant.featured_media && variant.featured_media.id) {
			this.goToMediaId(variant.featured_media.id);
		}

		// renderProductInfo() only swaps price / sku / inventory, so the
		// underlined colour name is ours to keep in sync.
		const colourIndex = parseInt(this.dataset.colorIndex, 10);
		if (colourIndex >= 0 && Array.isArray(variant.options)) {
			const label = document.getElementById(`PdpColorValue-${this.sectionId}`);
			if (label) label.textContent = variant.options[colourIndex];
		}
	}

	/**
	 * Pointer drag.
	 *
	 * Pointer capture is the important part: without it, releasing outside the
	 * track never delivers pointerup, so --pdp-drag stays applied and the whole
	 * carousel sits permanently off-centre.
	 */
	bindDrag() {
		const THRESHOLD = 6; // px before a press counts as a drag rather than a click
		let pointerId = null;
		let startX = 0;
		let startY = 0;
		let delta = 0;
		let dragging = false;
		let pitch = 0; // px of travel that equals one slide, measured per gesture

		/**
		 * Centre-to-centre distance between neighbouring slides.
		 *
		 * offsetWidth, not getBoundingClientRect: the latter includes the scale()
		 * on the peeks and would report the shrunken width.
		 *
		 * The --pdp-step factor is the part that is easy to miss. Slides are NOT
		 * one slide width apart — the layout overlaps them at 0.657 of a width,
		 * and every mobile breakpoint retunes that again. Treating a width as a
		 * step means the gesture needs 52% more travel than it looks like it
		 * should before anything moves, and a fling across several slides only
		 * advances one. Measured once per gesture so pointermove stays free of
		 * forced style recalcs.
		 */
		const measurePitch = () => {
			const width = this.slides[0].offsetWidth || this.track.clientWidth;
			const step = parseFloat(getComputedStyle(this).getPropertyValue('--pdp-step'));
			return width * (step > 0 ? step : 1);
		};

		/**
		 * 1:1 for the first slide of travel, then progressively stiffer, so a long
		 * pull cannot haul the whole strip off the side of the page. Asymptotic at
		 * two slides, and C1-continuous at the handover so there is no kink.
		 *
		 * The resistance is deliberately visual only — the step count below still
		 * comes from the raw pointer distance, so a long fling still travels as
		 * far as it was asked to.
		 */
		const resist = (dx) => {
			const over = Math.abs(dx) - pitch;
			if (over <= 0 || pitch <= 0) return dx;
			return Math.sign(dx) * (pitch + (over * pitch) / (over + pitch));
		};

		const finish = () => {
			if (pointerId === null) return;

			try {
				if (this.track.hasPointerCapture(pointerId)) {
					this.track.releasePointerCapture(pointerId);
				}
			} catch (e) {
				/* capture already lost */
			}
			pointerId = null;

			if (!dragging) return;
			dragging = false;

			this.removeAttribute('data-dragging');
			this.track.style.removeProperty('--pdp-drag');

			const steps = pitch > 0 ? Math.round(-delta / pitch) : 0;
			if (steps !== 0) this.goTo(this.index + steps);

			// Swallow the click this gesture is about to synthesise, so ending a
			// drag over the centre slide does not also open the zoom modal.
			this.suppressClick = true;
			window.setTimeout(() => {
				this.suppressClick = false;
			}, 0);
		};

		this.track.addEventListener('pointerdown', (event) => {
			if (event.pointerType === 'mouse' && event.button !== 0) return;
			if (pointerId !== null) return;
			pointerId = event.pointerId;
			startX = event.clientX;
			startY = event.clientY;
			delta = 0;
			dragging = false;
		});

		this.track.addEventListener('pointermove', (event) => {
			if (event.pointerId !== pointerId) return;

			const dx = event.clientX - startX;
			const dy = event.clientY - startY;

			if (!dragging) {
				if (Math.abs(dx) < THRESHOLD) return;
				// A mostly-vertical gesture is the page scrolling, not a slide drag.
				if (Math.abs(dy) > Math.abs(dx)) {
					pointerId = null;
					return;
				}
				dragging = true;
				pitch = measurePitch();
				this.setAttribute('data-dragging', '');
				try {
					this.track.setPointerCapture(pointerId);
				} catch (e) {
					/* not capturable; move handlers still fire on the track */
				}
			}

			delta = dx;
			this.track.style.setProperty('--pdp-drag', `${resist(dx)}px`);
		});

		this.track.addEventListener('pointerup', finish);
		this.track.addEventListener('pointercancel', finish);
		this.track.addEventListener('lostpointercapture', finish);

		// Native image dragging would otherwise steal the gesture.
		this.track.addEventListener('dragstart', (event) => event.preventDefault());
	}
}

if (!customElements.get('pdp-gallery')) {
	customElements.define('pdp-gallery', PdpGallery);
}
